import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PermissionGuard from './components/PermissionGuard';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import UserManagement from './pages/UserManagement';
import Attendance from './pages/Attendance';
import AttendanceManagement from './pages/AttendanceManagement';
import Leaves from './pages/Leaves';
import Payroll from './pages/Payroll';
import HMRC from './pages/HMRC';
import Reports from './pages/Reports';
import Schedules from './pages/Schedules';
import Settings from './pages/Settings';
import DynamicPermissionSettings from './pages/DynamicPermissionSettings';
import UnifiedRoleManagement from './pages/UnifiedRoleManagement';
import Kiosk from './pages/Kiosk';
import Organization from './pages/Organization';
import Documents from './pages/Documents';
import DocumentNotificationSettings from './pages/DocumentNotificationSettings';
import SSOSettings from './pages/SSOSettings';
import ProbationaryManagement from './pages/ProbationaryManagement';
import WorkflowManagement from './pages/WorkflowManagement';
import PendingApprovals from './pages/PendingApprovals';
import UKComplianceDashboard from './pages/UKComplianceDashboard';
import UKScheduling from './pages/UKScheduling';
import TimeAttendance from './pages/TimeAttendance';
import EnterpriseScheduling from './pages/EnterpriseScheduling';
import EnterpriseReporting from './pages/EnterpriseReporting';
import Portal from './pages/Portal';
import Tasks from './pages/Tasks';
import AnnouncementManagement from './pages/AnnouncementManagement';
import CalendarSync from './pages/CalendarSync';
import AnnouncementPopup from './components/AnnouncementPopup';
import SSOCallback from './components/SSOCallback';
import './index.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route component
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles.length > 0 && !hasPermission(requiredRoles)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <AnnouncementPopup />
      {children}
    </Layout>
  );
};

// Public Route component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// All page components are now imported from separate files

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <PermissionProvider>
            <Router>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />

              {/* SSO Callback - No wrapper, direct access */}
              <Route path="/sso-callback" element={<SSOCallback />} />
              
              <Route path="/reset-password" element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              } />
              
              <Route path="/kiosk" element={
                <PublicRoute>
                  <Kiosk />
                </PublicRoute>
              } />
              
              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Portal />
                </ProtectedRoute>
              } />

              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />

              <Route path="/portal" element={
                <ProtectedRoute>
                  <Portal />
                </ProtectedRoute>
              } />

              <Route path="/announcements" element={
                <ProtectedRoute requiredRoles={['admin', 'hr_manager']}>
                  <AnnouncementManagement />
                </ProtectedRoute>
              } />

              <Route path="/employees" element={
                <ProtectedRoute>
                  <PermissionGuard module="employees" action="view">
                    <Employees />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/user-management" element={
                <ProtectedRoute>
                  <PermissionGuard module="user_management" action="view">
                    <UserManagement />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/attendance" element={
                <ProtectedRoute>
                  <Attendance />
                </ProtectedRoute>
              } />
              
              <Route path="/attendance-management" element={
                <ProtectedRoute>
                  <PermissionGuard module="attendance_management" action="view">
                    <AttendanceManagement />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/leaves" element={
                <ProtectedRoute>
                  <PermissionGuard module="leave_requests" action="view">
                    <Leaves />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/calendar-sync" element={
                <ProtectedRoute>
                  <PermissionGuard module="calendar_sync" action="view">
                    <CalendarSync />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/payroll" element={
                <ProtectedRoute>
                  <PermissionGuard module="payroll" action="view">
                    <Payroll />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/hmrc" element={
                <ProtectedRoute>
                  <PermissionGuard module="hmrc" action="view">
                    <HMRC />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/reports" element={
                <ProtectedRoute>
                  <PermissionGuard module="reports" action="view">
                    <Reports />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/schedules" element={
                <ProtectedRoute>
                  <PermissionGuard module="schedules" action="view">
                    <Schedules />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/permission-settings" element={
                <ProtectedRoute>
                  <PermissionGuard module="permission_settings" action="view">
                    <DynamicPermissionSettings />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/organization/roles" element={
                <ProtectedRoute>
                  <PermissionGuard module="organization" action="manage">
                    <UnifiedRoleManagement />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/probationary" element={
                <ProtectedRoute>
                  <PermissionGuard module="probation" action="view">
                    <ProbationaryManagement />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/workflow-management/*" element={
                <ProtectedRoute>
                  <PermissionGuard module="workflow" action="view">
                    <WorkflowManagement />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/pending-approvals" element={
                <ProtectedRoute>
                  <PermissionGuard module="workflow" action="view">
                    <PendingApprovals />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/settings" element={
                <ProtectedRoute>
                  <PermissionGuard module="settings" action="view">
                    <Settings />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/sso-settings" element={
                <ProtectedRoute>
                  <PermissionGuard module="settings" action="edit">
                    <SSOSettings />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/organization/*" element={
                <ProtectedRoute>
                  <PermissionGuard module="organization" action="view">
                    <Organization />
                  </PermissionGuard>
                </ProtectedRoute>
              } />
              
              <Route path="/documents" element={
                <ProtectedRoute>
                  <Documents />
                </ProtectedRoute>
              } />
              
              <Route path="/document-settings" element={
                <ProtectedRoute>
                  <PermissionGuard module="documents" action="manage">
                    <DocumentNotificationSettings />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/uk-compliance-dashboard" element={
                <ProtectedRoute>
                  <PermissionGuard module="uk_compliance" action="view">
                    <UKComplianceDashboard />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/uk-scheduling" element={
                <ProtectedRoute>
                  <PermissionGuard module="uk_scheduling" action="view">
                    <UKScheduling />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/time-attendance" element={
                <ProtectedRoute>
                  <PermissionGuard module="time_attendance" action="view">
                    <TimeAttendance />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/enterprise-scheduling" element={
                <ProtectedRoute>
                  <PermissionGuard module="uk_scheduling" action="view">
                    <EnterpriseScheduling />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/enterprise-reporting" element={
                <ProtectedRoute>
                  <PermissionGuard module="reports" action="view">
                    <EnterpriseReporting />
                  </PermissionGuard>
                </ProtectedRoute>
              } />

              <Route path="/tasks" element={
                <ProtectedRoute>
                  <Tasks />
                </ProtectedRoute>
              } />

            </Routes>
          </div>
            </Router>
          </PermissionProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;